import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { motion } from 'framer-motion';
import { 
  SearchEnrichmentInsight, 
  SearchEnrichmentDebugInfo,
  SearchResultDebugInfo,
  TavilySearchResult 
} from '../../types';

interface SearchEnrichmentDevDisplayProps {
  insights: SearchEnrichmentInsight[];
  debugInfo?: SearchEnrichmentDebugInfo;
}

// Collapsible Section with production-matching styling
const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  badge?: string | number;
  badgeColor?: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, badge, badgeColor = 'bg-primary/20 text-primary', defaultExpanded = false, children }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div className="space-y-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left group"
      >
        <h4 className="text-sm font-medium text-text-secondary dark:text-dark-textSecondary flex items-center space-x-2">
          {icon}
          <span>{title}</span>
          {badge !== undefined && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${badgeColor} font-medium`}>
              {badge}
            </span>
          )}
        </h4>
        <svg 
          className={`w-4 h-4 text-text-tertiary dark:text-dark-textTertiary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isExpanded && (
        <div className="space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// Search Query Card - displays researcher agent queries
const SearchQueryCard: React.FC<{ query: { query: string; start_date?: string; end_date?: string }; index: number }> = ({ query, index }) => {
  const hasDateBounds = query.start_date || query.end_date;
  
  return (
    <div className="bg-white dark:bg-dark-secondary/50 rounded-lg border border-neutral-dark dark:border-dark-border border-l-4 border-l-purple-500 overflow-hidden">
      <div className="px-4 py-3">
        <div className="flex items-start space-x-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center text-xs font-bold">
            {index + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-text-primary dark:text-dark-textPrimary">{query.query}</p>
            {hasDateBounds && (
              <p className="text-xs text-text-tertiary dark:text-dark-textTertiary mt-1 flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{query.start_date || 'any'} → {query.end_date || 'now'}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Individual Tavily Result Card
const TavilyResultCard: React.FC<{ result: TavilySearchResult; index: number }> = ({ result, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-white dark:bg-dark-secondary/50 rounded-lg border border-neutral-dark dark:border-dark-border overflow-hidden">
      <div className="px-4 py-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-text-tertiary dark:text-dark-textTertiary font-mono">[{index + 1}]</span>
              <a 
                href={result.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline line-clamp-1"
                title={result.title}
              >
                {result.title}
              </a>
            </div>
            <p className="text-xs text-text-tertiary dark:text-dark-textTertiary mt-1 truncate">{result.url}</p>
            <div className="flex items-center flex-wrap gap-2 mt-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
                Score: {result.score.toFixed(3)}
              </span>
              {result.published_date && (
                <span className="text-xs text-text-tertiary dark:text-dark-textTertiary flex items-center space-x-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{result.published_date}</span>
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-neutral-dark/10 dark:hover:bg-dark-border/50 rounded transition-colors"
            aria-label={isExpanded ? 'Hide content' : 'Show content'}
          >
            <svg 
              className={`w-4 h-4 text-text-secondary dark:text-dark-textSecondary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-neutral-dark/30 dark:border-dark-border/30">
            <p className="text-xs text-text-secondary dark:text-dark-textSecondary whitespace-pre-wrap leading-relaxed">
              {result.content}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Search Result Group - results for a single query
const SearchResultGroup: React.FC<{ result: SearchResultDebugInfo; index: number }> = ({ result, index }) => {
  const [showAll, setShowAll] = useState(false);
  const displayResults = showAll ? result.results : result.results.slice(0, 2);
  
  return (
    <div className="bg-white dark:bg-dark-secondary/50 rounded-lg border border-neutral-dark dark:border-dark-border border-l-4 border-l-teal-500 overflow-hidden">
      <div className="px-4 py-3">
        {/* Query header */}
        <div className="flex items-start space-x-3 mb-3">
          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center text-xs font-bold">
            {index + 1}
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium text-text-primary dark:text-dark-textPrimary">{result.query}</p>
            <div className="flex items-center flex-wrap gap-2 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                {result.result_count} results
              </span>
              {(result.start_date || result.end_date) && (
                <span className="text-xs text-text-tertiary dark:text-dark-textTertiary">
                  📅 {result.start_date || 'any'} → {result.end_date || 'now'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Tavily Quick Answer */}
        {result.answer && (
          <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border-l-4 border-l-green-500">
            <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-1 flex items-center space-x-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Tavily Quick Answer</span>
            </p>
            <p className="text-sm text-text-primary dark:text-dark-textPrimary">{result.answer}</p>
          </div>
        )}
        
        {/* Individual results */}
        <div className="space-y-2">
          {displayResults.map((r, i) => (
            <TavilyResultCard key={i} result={r} index={i} />
          ))}
        </div>
        
        {/* Show more/less toggle */}
        {result.results.length > 2 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-3 w-full py-2 text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center justify-center space-x-1"
          >
            <span>{showAll ? 'Show Less' : `Show ${result.results.length - 2} More Results`}</span>
            <svg 
              className={`w-3 h-3 transition-transform ${showAll ? 'rotate-180' : ''}`}
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

// Modern Insight Card with 2025 AI UX - using app's design system glow utilities
const InsightCard: React.FC<{ insight: SearchEnrichmentInsight; variant: 'action' | 'clarification'; index: number }> = ({ insight, variant, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Copy to clipboard handler
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(insight.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Extract domain from URL for favicon
  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };
  
  // Check if content is long (more than ~200 chars)
  const isLongContent = insight.content.length > 200;
  
  // Color scheme based on variant - using primary shades for action, teal/cyan for context
  const colorScheme = variant === 'action' 
    ? {
        bgGradient: 'from-primary-500/5 via-primary-400/3 to-transparent',
        leftBar: 'from-primary-500 to-primary-700',
        badge: 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300',
        badgeIcon: 'from-primary-400 to-primary-600',
        hoverText: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300',
      }
    : {
        bgGradient: 'from-cyan-500/5 via-teal-400/3 to-transparent',
        leftBar: 'from-cyan-400 to-teal-600',
        badge: 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300',
        badgeIcon: 'from-cyan-400 to-teal-500',
        hoverText: 'text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300',
      };

  return (
    <div 
      className={`group relative rounded-xl overflow-hidden transition-all duration-300 ease-out
        bg-white dark:bg-dark-secondary/90 border border-neutral-dark dark:border-dark-border
        hover-glow-lg halo-container
        ${isHovered ? 'shadow-glow-md' : 'shadow-sm'}
        hover:-translate-y-0.5`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Subtle gradient overlay */}
      <div className={`absolute inset-0 opacity-[0.04] dark:opacity-[0.07] pointer-events-none
        bg-gradient-to-br ${colorScheme.bgGradient}`} 
      />
      
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 
        bg-gradient-to-b ${colorScheme.leftBar}`} 
      />
      
      {/* Content */}
      <div className="relative px-5 py-4 pl-6">
        {/* Top row: Number badge + Action buttons */}
        <div className="flex items-start justify-between mb-3">
          {/* Insight number with glow effect */}
          <div className="flex items-center space-x-2">
            <span className={`flex-shrink-0 w-7 h-7 rounded-lg 
              bg-gradient-to-br ${colorScheme.badgeIcon}
              text-white flex items-center justify-center text-xs font-bold
              shadow-md ${variant === 'action' ? 'shadow-primary/30' : 'shadow-cyan-500/30'}`}>
              {index + 1}
            </span>
            {/* Type badge */}
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full
              ${colorScheme.badge}`}>
              {variant === 'action' ? '⚡ Action' : '💡 Context'}
            </span>
          </div>
          
          {/* Hover action buttons */}
          <div className={`flex items-center space-x-1 transition-all duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <button
              onClick={handleCopy}
              className={`p-1.5 rounded-lg transition-all duration-200
                ${isCopied 
                  ? (variant === 'action' 
                    ? 'bg-primary/20 dark:bg-primary/30 text-primary dark:text-primary-light'
                    : 'bg-cyan-500/20 dark:bg-cyan-500/30 text-cyan-600 dark:text-cyan-400')
                  : 'hover:bg-neutral dark:hover:bg-dark-border/50 text-text-tertiary dark:text-dark-textTertiary hover:text-primary'}`}
              title={isCopied ? 'Copied!' : 'Copy insight'}
            >
              {isCopied ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Main content - simple content swap without broken height animation */}
        <div className="prose dark:prose-invert prose-neutral max-w-none 
          prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-text-primary prose-p:dark:text-dark-textPrimary prose-p:my-1
          prose-strong:text-text-primary prose-strong:dark:text-white prose-strong:font-semibold
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {isLongContent && !isExpanded 
              ? insight.content.slice(0, 180) + '...' 
              : insight.content}
          </ReactMarkdown>
        </div>
        
        {/* Expand/Collapse for long content */}
        {isLongContent && (
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`mt-2 text-xs font-medium ${colorScheme.hoverText}
              flex items-center space-x-1 transition-colors`}
            whileHover={{ x: 2 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>{isExpanded ? 'Show less' : 'Read more'}</span>
            <motion.svg 
              className="w-3 h-3"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </motion.svg>
          </motion.button>
        )}
        
        {/* Source with favicon and better styling */}
        {insight.source && (
          <div className="mt-4 pt-3 border-t border-neutral-dark/30 dark:border-dark-border/30">
            <a 
              href={insight.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-xs text-text-secondary dark:text-dark-textSecondary 
                hover:text-primary dark:hover:text-primary transition-all duration-200 group/link
                px-2.5 py-1.5 -mx-2.5 rounded-lg hover:bg-neutral/80 dark:hover:bg-dark-border/30"
            >
              {/* Favicon */}
              <img 
                src={`https://www.google.com/s2/favicons?domain=${getDomain(insight.source.url)}&sz=16`}
                alt=""
                className="w-4 h-4 rounded-sm"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <span className="truncate max-w-sm group-hover/link:text-primary transition-colors" title={insight.source.title}>
                {insight.source.title}
              </span>
              <svg className="w-3 h-3 flex-shrink-0 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const SearchEnrichmentDevDisplay: React.FC<SearchEnrichmentDevDisplayProps> = ({ insights, debugInfo }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Debug logging
  console.log('[SearchEnrichmentDevDisplay] Rendering with insights:', insights?.length, 'debugInfo:', debugInfo);
  
  if (!insights || insights.length === 0) {
    return null;
  }

  const actionInsights = insights.filter(i => i.type === 'action');
  const clarificationInsights = insights.filter(i => i.type === 'clarification');

  // Calculate stats
  const totalSearchResults = debugInfo?.search_results?.reduce((sum, r) => sum + r.result_count, 0) || 0;

  return (
    <div className="mt-6 rounded-xl overflow-hidden border-2 border-dashed border-primary/30 dark:border-primary/40 bg-gradient-to-br from-primary/5 to-transparent dark:from-primary/10">
      {/* Header - matching production style with DEV badge */}
      <div 
        className="flex justify-between items-center px-4 py-3 bg-primary/10 dark:bg-primary/20 cursor-pointer hover:bg-primary/15 dark:hover:bg-primary/25 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <svg 
            className="w-5 h-5 text-primary" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
          <h3 className="text-sm font-semibold text-primary dark:text-primary">
            Web Research Insights
          </h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400 font-medium">
            DEV
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
            {insights.length} insight{insights.length !== 1 ? 's' : ''}
          </span>
          {debugInfo?.execution_time_seconds && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
              ⏱ {debugInfo.execution_time_seconds.toFixed(1)}s
            </span>
          )}
        </div>
        <button 
          className="p-1 hover:bg-primary/20 rounded transition-colors"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg 
            className={`w-5 h-5 text-primary transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-6">
          
          {/* Pipeline Stats Bar */}
          {debugInfo && (
            <div className="flex flex-wrap items-center gap-3 p-3 bg-neutral-light dark:bg-dark-secondary rounded-lg text-xs">
              <span className="font-medium text-text-secondary dark:text-dark-textSecondary">Pipeline:</span>
              {debugInfo.researcher_model && (
                <span className="px-2 py-1 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  🔍 {debugInfo.researcher_model}
                </span>
              )}
              {debugInfo.search_queries && debugInfo.search_queries.length > 0 && (
                <span className="px-2 py-1 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  {debugInfo.search_queries.length} queries
                </span>
              )}
              {debugInfo.search_results && debugInfo.search_results.length > 0 && (
                <span className="px-2 py-1 rounded bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
                  {totalSearchResults} results
                </span>
              )}
              {debugInfo.reporter_model && (
                <span className="px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  📝 {debugInfo.reporter_model}
                </span>
              )}
            </div>
          )}

          {/* 1. Researcher Agent Queries */}
          {debugInfo?.search_queries && debugInfo.search_queries.length > 0 && (
            <CollapsibleSection 
              title="Researcher Agent Queries"
              icon={
                <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
              badge={debugInfo.search_queries.length}
              badgeColor="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
              defaultExpanded={false}
            >
              {debugInfo.search_queries.map((query, i) => (
                <SearchQueryCard key={i} query={query} index={i} />
              ))}
            </CollapsibleSection>
          )}

          {/* 2. Tavily Search Results */}
          {debugInfo?.search_results && debugInfo.search_results.length > 0 && (
            <CollapsibleSection 
              title="Tavily API Results"
              icon={
                <svg className="w-4 h-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              }
              badge={`${debugInfo.search_results.length} queries → ${totalSearchResults} results`}
              badgeColor="bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
              defaultExpanded={false}
            >
              {debugInfo.search_results.map((result, i) => (
                <SearchResultGroup key={i} result={result} index={i} />
              ))}
            </CollapsibleSection>
          )}

          {/* 3. Collated Context (optional, collapsed by default) */}
          {debugInfo?.collated_context && (
            <CollapsibleSection 
              title="Collated Context"
              icon={
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              badge={`${(debugInfo.collated_context.length / 1000).toFixed(1)}k chars`}
              badgeColor="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
              defaultExpanded={false}
            >
              <div className="max-h-64 overflow-y-auto rounded-lg">
                <pre className="text-xs text-text-secondary dark:text-dark-textSecondary whitespace-pre-wrap font-mono p-3 bg-neutral-light dark:bg-dark-secondary rounded">
                  {debugInfo.collated_context}
                </pre>
              </div>
            </CollapsibleSection>
          )}

          {/* Divider */}
          <hr className="border-neutral-dark/30 dark:border-dark-border/30" />

          {/* 4. Final Insights - Action */}
          {actionInsights.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary dark:text-dark-textPrimary flex items-center space-x-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-primary to-primary-dark text-white shadow-md shadow-primary/30">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                <span>Actionable Recommendations</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light font-medium">
                  {actionInsights.length}
                </span>
              </h4>
              <div className="space-y-4">
                {actionInsights.map((insight, idx) => (
                  <InsightCard key={insight.id} insight={insight} variant="action" index={idx} />
                ))}
              </div>
            </div>
          )}

          {/* 5. Final Insights - Clarification */}
          {clarificationInsights.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-text-primary dark:text-dark-textPrimary flex items-center space-x-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-primary-light to-primary text-white shadow-md shadow-primary/20">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </span>
                <span>Contextual Information</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/5 dark:bg-primary/15 text-primary/80 dark:text-primary-light/80 font-medium">
                  {clarificationInsights.length}
                </span>
              </h4>
              <div className="space-y-4">
                {clarificationInsights.map((insight, idx) => (
                  <InsightCard key={insight.id} insight={insight} variant="clarification" index={idx} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchEnrichmentDevDisplay;
